import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Radio, Spin, Tag, message } from 'antd';
import { useDispatch } from 'react-redux';
import CreditsIcon from 'lib.components/src/components/CreditsIcon';
import { useAuth } from 'lib.context/src/AuthContext';
import { UserBillingPageContext } from 'lib.context/src/UserBillingPageContext';
import { BillingSectionType } from 'lib.context/src/models/billing-section-types';
import { TextBodyRegular, TextFootnoteDescription } from 'lib.typography';
import { formatCurrencyAmount, formatFullNumber } from 'lib.shared/src/numberFormat';
import { showApiError } from 'lib.shared/src/handleApiError';
import {
  useGetCreditPacksQuery,
  usePostBuyCreditsMutation,
} from 'lib.store/src/main/billing/billing.api';
import { partnerCreativesDeskApi } from 'lib.store/src/type-partner/creatives-desk/creativesDesk.api';
import './styles.scss';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Buy credits (prototype v150): one line on what credits do, the packs to
 * pick from with the recommended one preselected, and one button that names
 * the charge. The card on file is charged — the billing drawers in front of
 * this window make sure there is one — and the credits land at once.
 */
const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const {
    user,
    reFetchUser,
    proSubscriptionData: { isProEnabled },
  } = useAuth();
  const { actualDrawerTypeHandler } = useContext(UserBillingPageContext);
  // credits come with Boost PRO (prototype v146: no PRO → the order window);
  // ours sends the sponsor to enable PRO first, packs come after
  const { data, isLoading, isError } = useGetCreditPacksQuery(undefined, {
    skip: !isOpen || !isProEnabled,
  });
  const [buyCredits, { isLoading: isBuying }] = usePostBuyCreditsMutation();
  const packs = useMemo(() => data?.data ?? [], [data]);
  const [picked, setPicked] = useState<number | null>(null);

  // the window opens on the recommended pack, and forgets the pick when closed
  useEffect(() => {
    if (!isOpen) {
      setPicked(null);
      return;
    }
    if (picked === null && packs.length) {
      setPicked((packs.find((pack) => pack.is_recommended) ?? packs[0]).credits);
    }
  }, [isOpen, packs, picked]);

  const pack = packs.find((item) => item.credits === picked) ?? null;
  const credits = Number(user?.organization?.credits?.total) || 0;
  // the charge runs in the billing currency, like a top-up — the window says so
  const currency = user?.organization?.billing?.currency || 'USD';
  const money = (value: number) =>
    formatCurrencyAmount(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const onEnablePro = () => {
    onClose();
    actualDrawerTypeHandler({ actionType: BillingSectionType.ENABLE_PRO, isModalView: false });
  };

  const onBuy = async () => {
    if (!pack) return;
    try {
      await buyCredits({ credits: pack.credits }).unwrap();
      // the header's credits and the desk's plan both read the new total —
      // silently, so the page under the window stays mounted
      await reFetchUser({ silent: true });
      dispatch(
        partnerCreativesDeskApi.util.invalidateTags([
          { type: 'partner_creatives_desk', id: 'PLAN' },
        ]),
      );
      void message.success(
        `${pack.credits} credits added — ${money(pack.price)} charged to the card on file`,
      );
      onClose();
    } catch (error) {
      // not a form: a 422 (a pack no longer on sale) must reach a toast
      showApiError(error, 'The charge did not go through — check the card on file', true);
    }
  };

  return (
    <Modal
      title="Buy credits"
      open={isOpen}
      width={520}
      onCancel={onClose}
      destroyOnClose
      footer={
        isProEnabled
          ? [
              <Button
                key="buy"
                type="primary"
                size="large"
                block
                disabled={!pack}
                loading={isBuying}
                onClick={() => void onBuy()}
                data-test-id="buy-credits-confirm"
              >
                {pack ? `Buy ${pack.credits} credits · ${money(pack.price)}` : 'Buy credits'}
              </Button>,
            ]
          : [
              <Button
                key="pro"
                type="primary"
                size="large"
                block
                onClick={onEnablePro}
                data-test-id="buy-credits-enable-pro"
              >
                Enable Boost PRO
              </Button>,
            ]
      }
    >
      <div className="buy-credits" data-test-id="buy-credits">
        <TextBodyRegular className="buy-credits__lead">
          Credits pay for new creatives and recreates. You have{' '}
          <b>{formatFullNumber(credits, { isRound: true })}</b>
          {isProEnabled
            ? ' — Boost PRO refills them monthly; a pack tops you up before then.'
            : '. Credits come with Boost PRO — a monthly refill, and packs on top when you need more.'}
        </TextBodyRegular>

        {!isProEnabled ? (
          <TextFootnoteDescription className="buy-credits__card">
            Enable Boost PRO first; the packs open up right after.
          </TextFootnoteDescription>
        ) : isLoading ? (
          <div className="buy-credits__loading">
            <Spin />
          </div>
        ) : isError ? (
          <Alert
            type="error"
            showIcon
            message="The packs could not be loaded — close the window and try again."
          />
        ) : (
          <Radio.Group
            className="buy-credits__packs"
            value={picked}
            onChange={(event) => setPicked(Number(event.target.value))}
          >
            {packs.map((item) => (
              <Radio.Button
                key={item.credits}
                value={item.credits}
                className="buy-credits__pack"
                data-test-id={`buy-credits-pack-${item.credits}`}
              >
                {item.is_recommended && (
                  <Tag color="blue" className="buy-credits__tag">
                    Most chosen
                  </Tag>
                )}
                <span className="buy-credits__count">
                  <CreditsIcon /> {item.credits}
                </span>
                <span className="buy-credits__price">{money(item.price)}</span>
                <TextFootnoteDescription className="buy-credits__per">
                  {formatCurrencyAmount(item.price / item.credits, currency)} a credit
                </TextFootnoteDescription>
              </Radio.Button>
            ))}
          </Radio.Group>
        )}

        {isProEnabled && (
          <TextFootnoteDescription className="buy-credits__card">
            Charged today to the card on file.
          </TextFootnoteDescription>
        )}
      </div>
    </Modal>
  );
};

export default BuyCreditsModal;
